import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ToastItem = {
  action?: { label: string; onClick: () => void };
  description?: string;
  id: number;
  title: string;
  variant?: "success" | "default";
};

const ToastContext = createContext<{
  pushToast: (toast: Omit<ToastItem, "id">) => void;
} | null>(null);

// `duration` is overridable so tests can pass Infinity to disable the
// auto-dismiss timer (Radix reads Infinity as "never schedule a timeout").
export function ToastProvider({
  children,
  duration = 3000,
}: {
  children: ReactNode;
  duration?: number;
}) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const value = useMemo(
    () => ({
      pushToast: (toast: Omit<ToastItem, "id">) => {
        setItems((current) => [
          ...current,
          { ...toast, id: Date.now() + current.length },
        ]);
      },
    }),
    [],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            className={`card w-[360px] rounded-2xl p-4 ${item.variant === "success" ? "border-emerald-200 bg-emerald-50" : ""}`}
            duration={duration}
            onOpenChange={(open) => {
              if (!open) {
                setItems((current) =>
                  current.filter((entry) => entry.id !== item.id),
                );
              }
            }}
            open
          >
            <div className="flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title
                  className={`text-sm font-semibold ${item.variant === "success" ? "text-emerald-800" : "text-pro-ink"}`}>
                  {item.title}
                </ToastPrimitive.Title>
                {item.description ? (
                  <ToastPrimitive.Description
                    className="mt-1 text-sm text-fg-muted"
                  >
                    {item.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              {item.action ? (
                <ToastPrimitive.Action asChild altText={item.action.label}>
                  <button
                    className="btn btn-default btn-sm ml-auto shrink-0"
                    type="button"
                    onClick={item.action.onClick}
                  >
                    {item.action.label}
                  </button>
                </ToastPrimitive.Action>
              ) : null}
              <ToastPrimitive.Close
                aria-label="Dismiss"
                className="rounded-full border-0 bg-transparent p-1 text-fg-muted"
                data-slot="interactive-control"
              >
                <X size={16} />
              </ToastPrimitive.Close>
            </div>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[60] flex max-w-full flex-col gap-3 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
